// apps/web/src/features/admin/automations/panel/BranchCard.tsx
import { Link, useParams } from 'react-router';
import type { AutomationBranch, AutomationBranchCheck, AutomationBranchRoute } from '@capture/shared';
import { describeCondition } from '@capture/shared';
import { ConditionGroupEditor } from '../../components/ConditionGroup';
import type { ConditionLookupTable } from '../../components/ConditionRow';
import type { FieldDefinitionSummary } from '../../hooks/useJobTypeFields';
import { CheckConfigBody, type RowCheckGroupOption } from '../CheckConfigBody';
import { RouteConfigBody } from '../RouteConfigBody';
import { CHECK_EFFECTS, ROUTE_EFFECTS, emptyConditionGroup, isConditionEmpty, defaultConfigForEffect } from '../effect-bucket';
import styles from './panel.module.css';

interface PipelineStepRow {
  stepType: string;
  label: string;
}

interface Props {
  branch: AutomationBranch;
  index: number;
  fields: FieldDefinitionSummary[];
  /** P2b — line-item groups on the form, for the row_check editor + its
   *  condition field picker (dotted `${groupId}.${col}` refs). */
  lineItemGroups?: RowCheckGroupOption[];
  lookupTables?: ConditionLookupTable[];
  /** P8 — job-level constant names, forwarded to the condition value picker. */
  constants?: string[];
  roles?: { slug: string; name: string }[];
  /** The tenant's active users — notify's target-user picker (Task 13). */
  users?: { id: string; name: string; email: string }[];
  /** E-054 P5 follow-up — the job's form classifications (key + form name),
   *  for the reclassify direct-target picker. */
  classifications?: { key: string; label: string }[];
  pipelineSteps: PipelineStepRow[];
  /** True only for a branch just added this session that hasn't had an
   *  outcome chip clicked yet — every chip (both families) renders enabled
   *  so any outcome, including a route, can be authored first (design:
   *  "family is locked by the FIRST outcome chosen"). */
  pristine?: boolean;
  /** Hide the remove (×) button entirely — the stepper's single-branch create
   *  flow has nothing to remove, so it never shows a no-op ×. */
  hideRemove?: boolean;
  /** Render the × disabled with a guiding tooltip — used for the only
   *  remaining branch (an automation must keep ≥1 if/then). */
  removeDisabled?: boolean;
  onChange: (next: AutomationBranch) => void;
  /** Fired when a chip commits the branch's first real outcome, so the
   *  parent can clear the pristine flag. Safe to call unconditionally. */
  onOutcomeCommitted?: () => void;
  onRemove: () => void;
}

const LAST_BRANCH_TOOLTIP =
  'An automation needs at least one if/then — delete the automation from the list instead';

/** design §5 chip vocabulary — the outcome names authors see, not the
 *  registry's internal effect labels (e.g. "Notify role", not "Notify"). */
const CHECK_CHIPS: { key: 'warn' | 'block'; label: string; severity: 'warning' | 'error' }[] = [
  { key: 'warn', label: 'Warn the operator', severity: 'warning' },
  { key: 'block', label: 'Block the operator', severity: 'error' },
];

// Design §5 chip copy — deliberately diverges from the registry's own
// labels (e.g. "Route to step" vs the registry's "Route to a queue/step").
// That divergence is intentional; a key with no override here falls back
// to the registry's label (see `routeChipLabel`) rather than going unlabeled.
const ROUTE_CHIP_LABELS: Record<string, string> = {
  route_to_queue: 'Route to step',
  hold: 'Hold',
  escalate: 'Escalate',
  notify: 'Notify role',
  reject: 'Reject',
  skip_step: 'Skip step',
  reclassify: 'Reclassify',
};

// Today's on-screen chip order predates the registry and doesn't match its
// declaration order — this array is a sort priority, not a hand-maintained
// key list, so it preserves the current visual order while the actual key
// set is derived from `ROUTE_EFFECTS` (a new 8th route effect appears
// automatically, sorted after these seven in registry order).
const ROUTE_CHIP_VISUAL_PRIORITY = ['route_to_queue', 'hold', 'escalate', 'notify', 'reject', 'skip_step', 'reclassify'];
const ROUTE_CHIP_ORDER = ROUTE_EFFECTS.map((e) => e.key).sort((a, b) => {
  const ai = ROUTE_CHIP_VISUAL_PRIORITY.indexOf(a);
  const bi = ROUTE_CHIP_VISUAL_PRIORITY.indexOf(b);
  if (ai === -1 && bi === -1) return 0;
  if (ai === -1) return 1;
  if (bi === -1) return -1;
  return ai - bi;
});

const CROSS_FAMILY_TOOLTIP =
  "One if/then fires one family of outcomes — add another if/then below for this.";

function routeChipLabel(effectKey: string): string {
  return ROUTE_CHIP_LABELS[effectKey] ?? ROUTE_EFFECTS.find((e) => e.key === effectKey)?.label ?? effectKey;
}

/** Compose (add) or drop a route outcome — "also…" (design §2). Never lets
 *  the last action disappear; a route branch always fires something. */
function toggleRouteAction(branch: AutomationBranchRoute, effectKey: string): AutomationBranchRoute {
  const exists = branch.actions.some((a) => a.effectKey === effectKey);
  if (exists) {
    if (branch.actions.length <= 1) return branch;
    return { ...branch, actions: branch.actions.filter((a) => a.effectKey !== effectKey) };
  }
  return { ...branch, actions: [...branch.actions, { effectKey, config: defaultConfigForEffect(effectKey) }] };
}

/** A pristine branch's first route chip click converts the check-shaped
 *  default into a real route branch, keeping the condition already
 *  authored. `ruleId` stays unset — the branch is still fresh. */
function convertToRouteBranch(branch: AutomationBranch, effectKey: string): AutomationBranchRoute {
  return {
    family: 'route',
    severity: 'error',
    condition: branch.condition,
    actions: [{ effectKey, config: defaultConfigForEffect(effectKey) ?? {} }],
  };
}

/**
 * One IF→THEN block. Family (check vs route) is fixed by the branch's own
 * discriminated-union shape once locked — chips for the OTHER family disable
 * with a tooltip (design §5). A *pristine* branch (freshly added, no outcome
 * chosen yet — see `pristine` prop) is the one exception: every chip in both
 * families renders enabled, and the first click either sets severity
 * in-place (check family) or converts the branch object to route family
 * (`convertToRouteBranch`), locking it from then on.
 * "Set a value" never mutates the branch; it links out to the field's
 * fill/transform editor (a different storage + condition system).
 */
export function BranchCard({
  branch,
  index,
  fields,
  lineItemGroups = [],
  lookupTables = [],
  constants = [],
  roles = [],
  users = [],
  classifications = [],
  pipelineSteps,
  pristine = false,
  hideRemove = false,
  removeDisabled = false,
  onChange,
  onOutcomeCommitted,
  onRemove,
}: Props) {
  const { jobTypeId = '' } = useParams<{ jobTypeId: string }>();
  const setValueHref = `/admin/job-types/${jobTypeId}?tab=automations`;
  const isCheck = branch.family === 'check';
  const checkBranch = isCheck ? (branch as AutomationBranchCheck) : undefined;
  const routeBranch = !isCheck ? (branch as AutomationBranchRoute) : undefined;

  // P2b — a row_check branch's condition references the group's columns as
  // dotted `${groupId}.${col}` refs; surface them as pickable fields (prepended
  // so they read first) alongside the doc fields. Runtime resolves the dotted
  // name via the per-row namespaced fields view.
  const rowCheckGroupId =
    checkBranch?.effectKey === 'row_check' && typeof checkBranch.config.rowGroup === 'string'
      ? (checkBranch.config.rowGroup as string)
      : '';
  const conditionFields = (() => {
    if (!rowCheckGroupId) return fields;
    const group = lineItemGroups.find((g) => g.groupId === rowCheckGroupId);
    if (!group) return fields;
    const dotted: FieldDefinitionSummary[] = group.columns.map((col) => ({
      id: `${group.groupId}.${col}`,
      name: `${group.groupId}.${col}`,
      label: `${group.name} › ${col}`,
      dataType: 'text',
      groupId: group.groupId,
      groupName: group.name,
    }));
    return [...dotted, ...fields];
  })();

  return (
    <div className={styles.branchCard} data-testid={`branch-card-${index}`}>
      <div className={styles.branchHeader}>
        <span className={styles.branchIndex}>Branch {index + 1}</span>
        {!hideRemove && (
          <button
            type="button"
            className={styles.branchRemove}
            onClick={onRemove}
            disabled={removeDisabled}
            title={removeDisabled ? LAST_BRANCH_TOOLTIP : undefined}
            aria-label={`Remove branch ${index + 1}`}
          >
            &times;
          </button>
        )}
      </div>

      <div className={styles.branchIf}>
        <span className={styles.branchLabel}>If</span>
        <span className={styles.branchSummary} data-testid={`branch-condition-summary-${index}`}>
          {describeCondition(branch.condition)}
        </span>
        <ConditionGroupEditor
          group={branch.condition ?? emptyConditionGroup()}
          fields={conditionFields}
          onChange={(group) => onChange({ ...branch, condition: isConditionEmpty(group) ? undefined : group })}
          compact
          lookupTables={lookupTables}
          constants={constants}
        />
      </div>

      <div className={styles.branchThen}>
        <span className={styles.branchLabel}>Then</span>
        <div className={styles.chipRow}>
          {CHECK_CHIPS.map((chip) => {
            const pressed = checkBranch?.severity === chip.severity;
            return (
              <button
                key={chip.key}
                type="button"
                className={styles.chip}
                aria-pressed={pressed}
                disabled={!isCheck}
                title={!isCheck ? CROSS_FAMILY_TOOLTIP : undefined}
                onClick={() => {
                  if (!checkBranch) return;
                  onChange({ ...checkBranch, severity: chip.severity });
                  onOutcomeCommitted?.();
                }}
              >
                {chip.label}
              </button>
            );
          })}
          {ROUTE_CHIP_ORDER.map((effectKey) => {
            const pressed = Boolean(routeBranch?.actions.some((a) => a.effectKey === effectKey));
            const disabled = isCheck && !pristine;
            return (
              <button
                key={effectKey}
                type="button"
                className={styles.chip}
                aria-pressed={pressed}
                disabled={disabled}
                title={disabled ? CROSS_FAMILY_TOOLTIP : undefined}
                onClick={() => {
                  if (routeBranch) {
                    onChange(toggleRouteAction(routeBranch, effectKey));
                    onOutcomeCommitted?.();
                    return;
                  }
                  if (isCheck && pristine) {
                    onChange(convertToRouteBranch(branch, effectKey));
                    onOutcomeCommitted?.();
                  }
                }}
              >
                {routeChipLabel(effectKey)}
              </button>
            );
          })}
          <Link to={setValueHref} className={styles.chip} data-testid={`branch-set-value-${index}`}>
            Set a value
          </Link>
        </div>

        {checkBranch && (
          <div className={styles.checkConfig}>
            <label className={styles.checkboxLabel}>
              <span>Check</span>
              <select
                data-testid={`branch-check-effect-${index}`}
                value={checkBranch.effectKey}
                onChange={(e) => {
                  const effectKey = e.target.value;
                  onChange({ ...checkBranch, effectKey, config: defaultConfigForEffect(effectKey) });
                }}
              >
                {CHECK_EFFECTS.map((ef) => (
                  <option key={ef.key} value={ef.key}>
                    {ef.label}
                  </option>
                ))}
              </select>
            </label>
            <CheckConfigBody
              effectKey={checkBranch.effectKey}
              config={checkBranch.config}
              onChange={(config) => onChange({ ...checkBranch, config })}
              fieldNames={fields.map((f) => f.name)}
              lineItemGroups={lineItemGroups}
            />
          </div>
        )}

        {routeBranch && (
          <div className={styles.routeConfig}>
            {routeBranch.actions.map((action, actionIndex) => (
              <div key={`${action.effectKey}-${actionIndex}`} className={styles.routeActionBlock}>
                <div className={styles.routeActionHeader}>
                  <span>{routeChipLabel(action.effectKey)}</span>
                  {routeBranch.actions.length > 1 && (
                    <button
                      type="button"
                      onClick={() =>
                        onChange({
                          ...routeBranch,
                          actions: routeBranch.actions.filter((_, i) => i !== actionIndex),
                        })
                      }
                      aria-label={`Remove ${routeChipLabel(action.effectKey)}`}
                    >
                      &times;
                    </button>
                  )}
                </div>
                <RouteConfigBody
                  effectKey={action.effectKey}
                  config={action.config}
                  pipelineSteps={pipelineSteps}
                  fieldNames={fields.map((f) => f.name)}
                  roles={roles}
                  users={users}
                  classifications={classifications}
                  onChange={(config) => {
                    const current = routeBranch.actions[actionIndex];
                    if (!current) return;
                    const actions = routeBranch.actions.slice();
                    actions[actionIndex] = { ...current, config };
                    onChange({ ...routeBranch, actions });
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
